import {weakMemo} from '@packages/utilities/memo';

import {type ExpressionPath} from '../ExpressionPath';
import {
  type Stoppable,
  _deconstructCall,
  _everything,
  _everythingWithContext,
  _everywhere,
  _everywhereWithContext,
  _getIn,
  _setIn,
  map,
  type EverywhereWithContextMapper,
  type EverythingWithContextMapper,
  type ResultCombiner,
  type Everything,
} from '../GenericExpression/GenericExpressionUtils';
import {type ExpressionF} from '../GenericExpression/GenericExpressionTypes';
import {Annotated, stripAnn, updateVal} from '@packages/utilities/annotated';
import {type Expression} from '@packages/systems/wfdl-ast';

export interface AnnotatedExpression<a> extends Annotated<
  a,
  ExpressionF<AnnotatedExpression<a>>
> {}

export const everywhere = <a>(
  fn: (arg1: AnnotatedExpression<a>) => AnnotatedExpression<a>
): ((arg1: AnnotatedExpression<a>) => AnnotatedExpression<a>) =>
  _everywhere<AnnotatedExpression<a>>(updateVal)(fn);

export const everywhereWithContext = <a, c>(
  contextMapper: EverywhereWithContextMapper<c, AnnotatedExpression<a>>
) => _everywhereWithContext<AnnotatedExpression<a>>(updateVal)(contextMapper);

export const everything = <a, r>(
  combine: ResultCombiner<r>
): ((
  arg1: (arg2: AnnotatedExpression<a>) => Stoppable<r>
) => (arg2: AnnotatedExpression<a>) => r) =>
  _everything<AnnotatedExpression<a>>(stripAnn)(combine);

export const everythingWithContext = <a, c, r>(
  initialContext: c
): ((
  resultCombiner: ResultCombiner<r>
) => (
  updaterFn: EverythingWithContextMapper<c, r, AnnotatedExpression<a>>
) => Everything<r, AnnotatedExpression<a>>) =>
  _everythingWithContext<AnnotatedExpression<a>>(stripAnn)(initialContext);

export const getIn = <a>(
  annExpr: AnnotatedExpression<a>,
  path: ExpressionPath
): undefined | AnnotatedExpression<a> =>
  _getIn<AnnotatedExpression<a>>(stripAnn)(annExpr, path);

export const setIn = <a>(
  annExpr: AnnotatedExpression<a>,
  path: ExpressionPath,
  nextAnnExpr: AnnotatedExpression<a>
): AnnotatedExpression<a> => {
  return _setIn<AnnotatedExpression<a>>(updateVal)(annExpr, path, nextAnnExpr);
};

// TODO: Avoid the extra wrapper call or use weakMemo in _deconstructCall
export const deconstructCall = <a>(
  annExpr: AnnotatedExpression<a>
): {
  fun: AnnotatedExpression<a>;
  args: Array<AnnotatedExpression<a>>;
} => _deconstructCall<AnnotatedExpression<a>>(stripAnn)(annExpr);

export const _stripAll = weakMemo(
  <a>(annExpr: AnnotatedExpression<a>): Expression =>
    map(_stripAll)(stripAnn(annExpr))
);
